import React, { useState } from 'react';
import AuditPlanUploadSection from './AuditPlanUploadSection';
import AuditPlanResultsSection from './AuditPlanResultsSection';

const AuditPlanContent = () => {
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleProcessingComplete = (processingResults) => {
    setResults(processingResults);
    setIsLoading(false);
  };

  const handleNewAnalysis = () => {
    setResults(null);
    setIsLoading(false);
  };

  return (
    <main className="p-6">
      <div className="space-y-8 pb-20">
      {/* Simple Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Audit Plan Analysis</h1>
        <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
          AI-powered analysis of single or multiple regulatory and internal documents for audit planning
        </p>
      </div>

      {/* Features Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-3 transition-colors duration-300">
        <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors duration-300">
          <i className="fa-solid fa-star mr-2 text-yellow-500 dark:text-yellow-400 text-xs"></i>
          Key Features
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="text-center">
            <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
              <i className="fa-solid fa-robot text-purple-600 dark:text-purple-400 text-xs transition-colors duration-300"></i>
            </div>
            <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">AI Document Processing</h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
              Pemrosesan dokumen langsung dengan teknologi AI canggih
            </p>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
              <i className="fa-solid fa-file-excel text-blue-600 dark:text-blue-400 text-xs transition-colors duration-300"></i>
            </div>
            <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Excel Export</h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
              Download results as CSV/Excel with proper formatting
            </p>
          </div>
          <div className="text-center">
            <div className="bg-green-100 dark:bg-green-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
              <i className="fa-solid fa-file-alt text-green-600 dark:text-green-400 text-xs transition-colors duration-300"></i>
            </div>
            <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Multiple Formats</h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
              Support for PDF, DOC, DOCX, and TXT files
            </p>
          </div>
          <div className="text-center">
            <div className="bg-red-100 dark:bg-red-900/30 rounded-full w-6 h-6 flex items-center justify-center mx-auto mb-1.5 transition-colors duration-300">
              <i className="fa-solid fa-shield-alt text-red-600 dark:text-red-400 text-xs transition-colors duration-300"></i>
            </div>
            <h3 className="font-medium text-gray-800 dark:text-white mb-0.5 text-xs transition-colors duration-300">Risk Assessment</h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight transition-colors duration-300">
              Identify critical processes and compliance requirements
            </p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <AuditPlanUploadSection 
        onProcessingComplete={handleProcessingComplete}
      />

      {/* Results Section */}
      {results && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white transition-colors duration-300">Analysis Results</h2>
            <button
              onClick={handleNewAnalysis}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center"
            >
              <i className="fa-solid fa-plus mr-2"></i>
              New Analysis
            </button>
          </div>
          <AuditPlanResultsSection results={results} />
        </>
      )}

      {/* Help Section */}
      {!results && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 transition-colors duration-300">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-question-circle mr-2 text-blue-500 dark:text-blue-400"></i>
            How to Use
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">Report 1 - Regulatory Analysis:</h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                <li>• Upload single or multiple OJK/BI regulatory documents (POJK, PBI, PADG)</li>
                <li>• Select "Report 1" to analyze regulatory requirements</li>
                <li>• Get two structured tables: Audit Wajib Tahunan & Area Teknologi</li>
                <li>• Extract compliance requirements and testing frequencies</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">Report 2 - High Risk Analysis:</h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 transition-colors duration-300">
                <li>• Upload single or multiple internal documents (SOP, WI, Policy, Guidelines)</li>
                <li>• Select "Report 2" to identify high-risk processes</li>
                <li>• Get table of processes with high operational/compliance risk</li>
                <li>• Includes impact analysis and existing controls information</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      </div>
    </main>
  );
};

export default AuditPlanContent;
